import makeStyles from '@mui/styles/makeStyles'
import { Box, TextField, Theme, Typography } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import React from 'react'
import { useApiErrors } from '../../../../hooks/useApiErrors'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutationCreateSpell, mutationEditSpell } from '../../../../api/mutations'
import { spellsQueryKey } from '../../../../api/queries'
import { DialogActions, DialogContent } from '../../../../components/Modal/Modal'
import { FormRow } from '../../../../components/FormRow/FormRow'
import { ErrorExplanation } from '../../../../components/ErrorExplanation/ErrorExplanation'
import { SubmitButton } from '../../../../components/SubmitButton/SubmitButton'
import { ISpell } from '../../../../api/types/spell'
import { EffectFormAttributes } from '../../../../components/EffectFormAttributes/EffectFormAttributes'

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      margin: theme.spacing(0.5),
    },
  })
)

interface Props {
  spell?: ISpell
  afterSuccess?: () => void
}

export const SpellForm: React.FC<Props> = ({ spell, afterSuccess }) => {
  const { apiErrors, setApiErrors, setApiErrorsFromAxios } = useApiErrors()
  const { register, handleSubmit, errors } = useForm<ISpell>()
  const queryClient = useQueryClient()

  const { mutate: executeMutation, isLoading: isMutating } = useMutation(
    (spell: ISpell) => (spell.id ? mutationEditSpell(spell.id, spell) : mutationCreateSpell(spell)),
    {
      onError: (err: any) => {
        setApiErrorsFromAxios(err, `Nie udało się ${spell?.id ? 'zmodyfikować' : 'stworzyć'} czaru`)
      },
      onSuccess: async () => {
        setApiErrors([])
        queryClient.invalidateQueries(spellsQueryKey()).then(afterSuccess)
      },
    }
  )
  const onSubmit = (data: ISpell) => executeMutation(data)

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {spell?.id && <input type="hidden" name="id" value={spell?.id} ref={register()} />}

        <DialogContent dividers>
          <FormRow>
            <ErrorExplanation errors={apiErrors} />
          </FormRow>
          <TextField
            name="school"
            label="Szkoła"
            fullWidth={true}
            variant="outlined"
            defaultValue={spell?.school}
            inputProps={{
              ref: register({
                required: 'Wymagane',
              }),
            }}
            error={!!errors.school}
            helperText={errors.school && errors.school.message}
          />

          <Box sx={{ marginBottom: '8px' }}>
            <Typography variant="button" display="block" gutterBottom>
              Podstawowe atrybuty
            </Typography>
            <Box sx={{ marginBottom: '8px' }}>
              <TextField
                name="name"
                label="Nazwa"
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.name}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.name}
                helperText={errors.name && errors.name.message}
              />
            </Box>

            <Box sx={{ marginBottom: '8px' }}>
              <TextField
                multiline
                name="description"
                label="Opis"
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.description}
                rows={4}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.description}
                helperText={errors.description && errors.description.message}
              />
            </Box>

            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <TextField
                sx={{ marginRight: '8px' }}
                name="circle"
                label="Krąg"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.circle}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.circle}
                helperText={errors.circle && errors.circle.message}
              />
              <TextField
                name="school"
                label="Szkoła"
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.school}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.school}
                helperText={errors.school && errors.school.message}
              />
            </Box>

            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <TextField
                name="target"
                label="Cel"
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.target}
                inputProps={{
                  ref: register(),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <TextField
                name="component"
                label="Wymagany komponent"
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.component}
                inputProps={{
                  ref: register(),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <TextField
                sx={{ marginRight: '8px' }}
                name="duration_turns"
                label="Czas trwania (tury)"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.duration_turns}
                inputProps={{
                  ref: register(),
                }}
              />
              <TextField
                name="range"
                label="Zasięg"
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.range}
                inputProps={{
                  ref: register(),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', marginBottom: '8px' }}>
              <TextField
                sx={{ marginRight: '8px' }}
                name="mana"
                label="Punkty skupienia"
                type={'number'}
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.mana}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.mana}
                helperText={errors.mana && errors.mana.message}
              />
              <TextField
                name="action_points"
                label="Punkty akcji"
                fullWidth={true}
                variant="outlined"
                defaultValue={spell?.action_points}
                inputProps={{
                  ref: register({
                    required: 'Wymagane',
                  }),
                }}
                error={!!errors.action_points}
                helperText={errors.action_points && errors.action_points.message}
              />
            </Box>

            <EffectFormAttributes effect={spell?.effect} register={register} errors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <SubmitButton disabled={isMutating}>{spell?.id ? 'Aktualizuj' : 'Stwórz'}</SubmitButton>
        </DialogActions>
      </form>
    </>
  )
}
